/**
 * The public signup form's rules (PRE-72), pure, so today's page, the new design's rebuild and the unit tests all ask
 * the same functions: a US phone to E.164, the password's strength, and whether the form may be sent.
 */
import { SIGNUP_WORDS } from '../../components/auth/signupWords';

const E = SIGNUP_WORDS.errors;

/**
 * Normalize a user-supplied US phone number to E.164.
 * Accepts common formats: "(628) 444-4018", "628-444-4018", "16284444018",
 * "+1 628 444 4018". Returns null if the input is not a valid 10-digit US
 * number (ignoring an optional leading 1).
 */
export function normalizePhoneE164(raw: string): string | null {
  if (typeof raw !== 'string') return null;
  const digits = raw.replace(/\D+/g, '');
  // 10 digits: US number without country code
  if (digits.length === 10) return `+1${digits}`;
  // 11 digits starting with 1: US number with country code
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return null;
}

/**
 * Validate password per PRE-72 spec: min 10 chars, mixed case (at least one
 * lowercase AND one uppercase), at least one digit.
 */
export function validatePassword(pw: string): { ok: boolean; reason?: string } {
  if (!pw || pw.length < 10) return { ok: false, reason: E.passwordLength };
  if (!/[a-z]/.test(pw)) return { ok: false, reason: E.passwordLower };
  if (!/[A-Z]/.test(pw)) return { ok: false, reason: E.passwordUpper };
  if (!/\d/.test(pw)) return { ok: false, reason: E.passwordDigit };
  return { ok: true };
}

/**
 * Whether the signup form may be sent (PRE-72). Submit is enabled ONLY when:
 *   - the terms box is ticked (SMS consent is OPTIONAL: Twilio A2P rule 30923 says consent cannot be a required
 *     condition of service),
 *   - all required text fields are non-empty,
 *   - the phone parses to a valid E.164 US number,
 *   - the password meets the strength requirements.
 */
export function isSignupSubmittable(input: {
  name: string;
  email: string;
  phone: string;
  password: string;
  smsConsent: boolean;
  tosAccepted: boolean;
}): boolean {
  if (!input.tosAccepted) return false;
  if (!input.name.trim()) return false;
  if (!input.email.trim()) return false;
  if (!input.phone.trim()) return false;
  if (normalizePhoneE164(input.phone) == null) return false;
  if (!validatePassword(input.password).ok) return false;
  return true;
}
